import React, { useEffect, useState } from 'react';
import { PieChart, Info, ShieldCheck, TrendingUp, AlertTriangle } from 'lucide-react';
import { api } from '../api';

function fmtPct(n) { if (n == null) return '—'; return `${Number(n).toFixed(1)}%`; }
function fmtInt(n) { if (n == null) return '—'; return Math.round(Number(n)).toLocaleString(); }
function fmtCensus(n) { if (n == null) return '—'; return Number(n).toFixed(1); }

// SVG donut chart for payer mix
function PayerDonut({ slices, size = 180 }) {
  const total = slices.reduce((s, x) => s + (x.value || 0), 0) || 1;
  const cx = size / 2, cy = size / 2, r = size / 2 - 14, sw = 22;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="payer-donut">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F0E7D2" strokeWidth={sw} />
      {slices.map((s) => {
        const frac = (s.value || 0) / total;
        const dash = 2 * Math.PI * r * frac;
        const gap = 2 * Math.PI * r - dash;
        const off = -acc;
        acc += dash;
        return (
          <circle
            key={s.label}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={sw}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={off}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
      })}
      <text x={cx} y={cy - 3} textAnchor="middle" style={{ fontSize: 11, fill: '#7A857F' }}>Total census</text>
      <text x={cx} y={cy + 16} textAnchor="middle" style={{ fontSize: 22, fontWeight: 600, fill: '#1A2622' }}>
        {fmtCensus(total)}
      </text>
    </svg>
  );
}

// Horizontal bar for skilled vs LTC split
function SkilledLtcBar({ skilledPct, ltcPct }) {
  const sk = Math.max(0, Math.min(100, skilledPct || 0));
  return (
    <div className="w-full">
      <div className="flex items-center justify-between text-[11px] text-[#5A6660] mb-1">
        <span className="font-semibold text-[#34538F]">Short-stay skilled · {fmtPct(skilledPct)}</span>
        <span className="font-semibold text-[#A06628]">Long-term care · {fmtPct(ltcPct)}</span>
      </div>
      <div className="h-7 rounded-md overflow-hidden bg-[#F0E7D2] flex">
        <div className="bg-[#34538F] flex items-center justify-center text-[11px] text-white font-semibold" style={{ width: `${sk}%` }}>
          {sk >= 12 ? `${Math.round(sk)}%` : ''}
        </div>
        <div className="bg-[#D58668] flex items-center justify-center text-[11px] text-white font-semibold" style={{ width: `${100 - sk}%` }}>
          {100 - sk >= 12 ? `${Math.round(100 - sk)}%` : ''}
        </div>
      </div>
    </div>
  );
}

function ConfidenceBadge({ confidence, caveats }) {
  const style = {
    high:   { bg: '#E5F0EA', fg: '#2D5F4F', label: 'high confidence' },
    medium: { bg: '#FBE6CC', fg: '#A06628', label: 'medium confidence' },
    low:    { bg: '#F0E7D2', fg: '#5A6660', label: 'low confidence' },
  }[confidence] || { bg: '#F0E7D2', fg: '#5A6660', label: 'unknown' };
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10.5px] font-semibold"
      style={{ background: style.bg, color: style.fg }}
      title={caveats && caveats.length ? caveats.join(' · ') : 'cost-report freshness + county MA coverage'}
    >
      <ShieldCheck size={10} /> {style.label}
    </span>
  );
}

function BenchRow({ label, value, county, state }) {
  return (
    <tr className="border-t border-[#F0E7D2]">
      <td className="px-3 py-1.5 text-[#3A4A44]">{label}</td>
      <td className="px-3 py-1.5 text-right font-semibold text-[#1A2622] tabular-nums">{fmtPct(value)}</td>
      <td className="px-3 py-1.5 text-right text-[#7A857F] tabular-nums">{fmtPct(county)}</td>
      <td className="px-3 py-1.5 text-right text-[#A8B0AA] tabular-nums">{fmtPct(state)}</td>
    </tr>
  );
}

export default function CensusMixTab({ ccn }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let stop = false;
    setLoading(true);
    api.facilityCensus(ccn)
      .then((d) => { if (!stop) { setData(d); setLoading(false); } })
      .catch((e) => { if (!stop) { setError(e?.response?.data?.detail || 'No census estimate available.'); setLoading(false); } });
    return () => { stop = true; };
  }, [ccn]);

  if (loading) return <div className="mt-7 rounded-xl bg-white border border-[#E5DCC4] p-8 text-[13px] text-[#7A857F]" data-testid="census-loading">Loading census estimate…</div>;
  if (error || !data) {
    return (
      <div className="mt-7 rounded-xl bg-white border border-dashed border-[#E5DCC4] p-8 text-center" data-testid="census-empty">
        <PieChart size={22} className="text-[#A8B0AA] mx-auto" />
        <div className="mt-3 text-[13px] text-[#7A857F]">{error}</div>
      </div>
    );
  }

  const c = data.census || {};
  const bc = data.benchmark_county || {};
  const bs = data.benchmark_state || {};

  const slices = [
    { label: 'Medicare',            value: c.medicare_census,            color: '#34538F' },
    { label: 'Managed Care (est.)', value: c.managed_care_census_est,    color: '#5C82C0' },
    { label: 'Medicaid',            value: c.medicaid_census,            color: '#A06628' },
    { label: 'Private Pay (est.)',  value: c.private_pay_census_est,     color: '#D58668' },
  ];

  return (
    <section className="mt-7" data-testid="census-tab">
      <div className="rounded-xl bg-white border border-[#E5DCC4] p-5 lg:p-6">
        {/* Top: opportunity score + confidence + caveats */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-[#E0EAF8] text-[#34538F]">
                <PieChart size={12} /> Estimated payer & census mix
              </span>
              <ConfidenceBadge confidence={data.confidence} caveats={data.caveats} />
              {data.fy_end && <span className="text-[11px] text-[#7A857F]">FY ending {new Date(data.fy_end).toLocaleDateString()}</span>}
            </div>
            <p className="mt-3 text-[13px] text-[#3A4A44] leading-relaxed max-w-[680px]">
              Based on this facility's most recent CMS cost report, we estimate
              <strong> {fmtPct(c.pct_skilled_est)}</strong> short-stay skilled census and
              <strong> {fmtPct(c.pct_ltc_est)}</strong> long-term-care census.
              Occupancy is approximately <strong>{fmtPct(c.occupancy_pct)}</strong>
              {data.opportunity_score != null && <> · Census Opportunity Score <strong>{data.opportunity_score?.toFixed(0)}/100</strong></>}.
            </p>
            {data.caveats?.length > 0 && (
              <div className="mt-2 flex items-start gap-1.5 text-[11px] text-[#A06628]">
                <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                <span>{data.caveats.join(' · ')}</span>
              </div>
            )}
          </div>
          {data.opportunity_score != null && (
            <div className="text-right">
              <div className="font-display text-4xl text-[#34538F] tabular-nums">{data.opportunity_score?.toFixed(0)}</div>
              <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">opportunity score</div>
              {data.sub_scores && (
                <div className="mt-1 text-[10px] text-[#A8B0AA]">
                  Occ {data.sub_scores.occupancy?.toFixed(0)} · Sk {data.sub_scores.skilled_mix?.toFixed(0)} · MC-gap {data.sub_scores.mc_growth_gap?.toFixed(0)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mid: donut + skilled-vs-LTC bar */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="flex justify-center md:justify-start">
            <PayerDonut slices={slices} />
          </div>
          <div className="md:col-span-2 space-y-4">
            <SkilledLtcBar skilledPct={c.pct_skilled_est} ltcPct={c.pct_ltc_est} />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {slices.map((s) => (
                <div key={s.label} className="flex items-center gap-2 text-[11.5px]">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ background: s.color }} />
                  <div>
                    <div className="font-semibold text-[#1A2622]">{s.label}</div>
                    <div className="text-[10.5px] text-[#7A857F]">
                      ~{fmtCensus(s.value)} residents
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Certified beds',           value: fmtInt(c.beds) },
          { label: 'Est. total census',        value: fmtCensus(c.avg_total_census), accent: 'text-[#1A2622]' },
          { label: 'Est. occupancy',           value: fmtPct(c.occupancy_pct),       accent: 'text-[#34538F]' },
          { label: 'Est. skilled mix',         value: fmtPct(c.pct_skilled_est),     accent: 'text-[#34538F]', sub: `${fmtCensus(c.skilled_census_est)} residents` },
          { label: 'Est. LTC mix',             value: fmtPct(c.pct_ltc_est),         accent: 'text-[#A06628]', sub: `${fmtCensus(c.ltc_census_est)} residents` },
        ].map((k) => (
          <div key={k.label} className="p-3 rounded-xl bg-white border border-[#E5DCC4]">
            <div className={`font-display text-xl ${k.accent || 'text-[#1A2622]'}`}>{k.value}</div>
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F] mt-0.5">{k.label}</div>
            {k.sub && <div className="text-[10px] text-[#A8B0AA] mt-0.5">{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Benchmark comparison */}
      <div className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#F0E7D2]">
          <h3 className="font-display text-base font-semibold text-[#1A2622]">Facility vs market benchmark</h3>
          <p className="text-[11.5px] text-[#7A857F]">How this facility's payer mix compares to {bc?.county ? `${bc.county}` : 'county'} and {bs?.state ? bs.state : 'state'} averages.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
              <tr>
                <th className="text-left px-3 py-2">Payer / metric</th>
                <th className="text-right px-3 py-2">This facility</th>
                <th className="text-right px-3 py-2">County avg</th>
                <th className="text-right px-3 py-2">State avg</th>
              </tr>
            </thead>
            <tbody>
              <BenchRow label="Occupancy"           value={c.occupancy_pct}        county={bc.avg_occupancy}        state={bs.avg_occupancy} />
              <BenchRow label="Short-stay skilled"  value={c.pct_skilled_est}      county={bc.avg_pct_skilled}      state={bs.avg_pct_skilled} />
              <BenchRow label="Long-term care"      value={c.pct_ltc_est}          county={bc.avg_pct_ltc}          state={bs.avg_pct_ltc} />
              <BenchRow label="Medicare share"      value={c.pct_medicare}         county={bc.avg_pct_medicare}     state={bs.avg_pct_medicare} />
              <BenchRow label="Medicaid share"      value={c.pct_medicaid}         county={bc.avg_pct_medicaid}     state={bs.avg_pct_medicaid} />
              <BenchRow label="Managed Care (est.)" value={c.pct_managed_care_est} county={bc.avg_pct_managed_care} state={bs.avg_pct_managed_care} />
              <BenchRow label="Private Pay (est.)"  value={c.pct_private_pay_est}  county={bc.avg_pct_private_pay}  state={bs.avg_pct_private_pay} />
            </tbody>
          </table>
        </div>
      </div>

      {/* Raw "Other" disclosure (compliance: show user the unapportioned bucket too) */}
      <div className="mt-4 rounded-xl bg-[#FAF5E6] border border-[#E5DCC4] p-4 text-[12px] text-[#3A4A44] leading-relaxed">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Info size={12} className="text-[#7A857F]" />
          <strong className="text-[#1A2622]">Raw "Other days" bucket (unapportioned):</strong>
        </div>
        Cost report reports <strong>{fmtInt(c.other_days)} days</strong> as "Other" (~{fmtCensus(c.other_census)} residents).
        We apportion this into <strong>{fmtCensus(c.managed_care_census_est)} Managed Care</strong> and <strong>{fmtCensus(c.private_pay_census_est)} Private Pay</strong> using
        <strong> {fmtPct(c.county_ma_pct_used)}</strong> county MA penetration × 70%. If you'd prefer to see "Other" undivided, treat the green/orange columns as
        a single bucket. The raw numbers above are the only directly-reported values.
      </div>

      {/* Trend / opportunity callout */}
      {data.sub_scores?.mc_growth_gap > 5 && (
        <div className="mt-4 rounded-xl bg-[#E0EAF8] border border-[#5C82C0] p-4 text-[12.5px] text-[#1A2622]" data-testid="mc-opportunity-callout">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp size={13} className="text-[#34538F]" />
            <strong>Managed Care opportunity</strong>
          </div>
          County MA penetration is <strong>{fmtPct(c.county_ma_pct_used)}</strong> but this facility's estimated MC share is only
          <strong> {fmtPct(c.pct_managed_care_est)}</strong>. There may be room to grow MC contracts and capture additional post-acute volume.
        </div>
      )}

      {/* Methodology + source */}
      <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
        <Info size={12} className="mt-0.5 shrink-0" />
        <span>
          <strong>Methodology:</strong> {data.methodology}{' '}
          <strong>Source:</strong> {data.source}.
          {data.updated_at && <> Updated {new Date(data.updated_at).toLocaleDateString()}.</>}
          {' '}<ShieldCheck size={11} className="inline mx-1 text-[#2D5F4F]" />
          All values are <strong>public-data estimates</strong>. No PHI; no patient-level data.
        </span>
      </div>
    </section>
  );
}
